import { _ as __rest, a as __assign } from './tslib.es6-01f7d394.js';
import { c as classNames } from './index-8a7d9ef9.js';
import * as React from 'react';
import React__default from 'react';
import { Link } from 'react-router-dom';
import { s as skeletonStyles } from './skeleton.module-e159ab57.js';
import { u as useTheme } from './ThemeProvider-2901309f.js';
import { d as defaultTheme } from './colors-8ec9a052.js';

function ReloadIcon(_a) {
    var _b = _a.fillColor, fillColor = _b === void 0 ? "currentColor" : _b, props = __rest(_a, ["fillColor"]);
    return (React__default.createElement("svg", __assign({ viewBox: "0 0 16 16", height: "16", width: "16" }, props),
        React__default.createElement("path", { fill: fillColor, d: "M10.43 4.064l.86-1c-1.19-.868-2.643-1.268-4.163-1.068-3.7.467-6.21 4.201-4.888 7.936.594 1.8 2.048 3.2 3.831 3.734 2.907.867 5.748-.467 7.07-2.867.131-.267.528-.4.858-.267l.594.267c.33.133.529.6.33.933-1.188 2.334-3.5 3.935-6.077 4.201-.33.067-.66.067-.925.067C3.956 16 .521 13 .06 8.932-.47 4.53 2.635.529 6.995.062c2.048-.266 4.096.334 5.682 1.534L13.602.53c.198-.2.528-.133.594.134l.991 4.067c.066.2-.132.467-.33.4l-4.096-.533c-.397 0-.529-.333-.33-.533z" })));
}

var styles = {"button":"Button_button_7747595d","noShadow":"Button_noShadow_7747595d","label":"Button_label_7747595d","focus-visible":"Button_focus-visible_7747595d","white":"Button_white_7747595d","disabled":"Button_disabled_7747595d","secondary":"Button_secondary_7747595d","tertiary":"Button_tertiary_7747595d","interface":"Button_interface_7747595d","skeleton":"Button_skeleton_7747595d","loading":"Button_loading_7747595d","icon":"Button_icon_7747595d","rotate":"Button_rotate_7747595d","children":"Button_children_7747595d","size-large":"Button_size-large_7747595d","size-small":"Button_size-small_7747595d","size-extra-small":"Button_size-extra-small_7747595d","spinner-left":"Button_spinner-left_7747595d","spinner-right":"Button_spinner-right_7747595d"};

function isPropsForAnchor(props) {
    return "href" in props;
}
function isPropsForLink(props) {
    return "to" in props;
}
var Button = React.forwardRef(function Button(props, ref) {
    var _a;
    var className = props.className, isLoading = props.isLoading, _b = props.variant, variant = _b === void 0 ? "primary" : _b, _c = props.size, size = _c === void 0 ? "medium" : _c, label = props.label, _d = props.color, colorProp = _d === void 0 ? "action" : _d, isSkeleton = props.isSkeleton, disabled = props.disabled, href = props.href, to = props.to, children = props.children, style = props.style, noShadow = props.noShadow, _e = props.spinnerAlign, spinnerAlign = _e === void 0 ? "center" : _e, _f = props.labelColor, labelProp = _f === void 0 ? "action" : _f, buttonProps = __rest(props, ["className", "isLoading", "variant", "size", "label", "color", "isSkeleton", "disabled", "href", "to", "children", "style", "noShadow", "spinnerAlign", "labelColor"]);
    var isDisabled = disabled || isSkeleton;
    var colors = useTheme().colors;
    var color = React.useMemo(function () {
        // In case a non-default color is passed in, use action
        if (!Object.keys(defaultTheme).includes(colorProp) &&
            "interface" !== colorProp &&
            "white" !== colorProp) {
            return "action";
        }
        return colorProp;
    }, [colorProp]);
    var labelColor = React.useMemo(function () {
        if (!labelProp) {
            return "action";
        }
        return labelProp;
    }, [labelProp]);
    var labelPropIsOrchardColor = Object.keys(defaultTheme).includes(labelProp) ||
        "interface" === labelProp;
    var hasShadow = !noShadow && variant !== "tertiary" && !isDisabled;
    var buttonClassNames = classNames(styles.button, styles["size-".concat(size)], styles["spinner-".concat(spinnerAlign)], (_a = {},
        _a[styles.loading] = isLoading,
        _a[styles.disabled] = isDisabled,
        _a[styles.secondary] = variant === "secondary",
        _a[styles.tertiary] = variant === "tertiary",
        _a[styles.interface] = color === "interface",
        _a[styles.white] = color === "white",
        _a[skeletonStyles.skeleton] = isSkeleton,
        _a[styles.skeleton] = isSkeleton,
        _a[styles.noShadow] = !hasShadow,
        _a), className);
    var buttonCssColors = React.useMemo(function () {
        if (color === "interface") {
            return {
                primary: colors["color-ui-interface-10"],
                label: colors["color-ui-interface"],
                labelHover: colors["color-ui-interface-mid"],
                labelActive: colors["color-ui-interface-dark"],
                altHover: colors["color-ui-interface-5"],
                altActive: colors["color-ui-interface-10"],
                hover: colors["color-ui-interface-20"],
                active: colors["color-ui-interface-40"],
            };
        }
        if (color === "white") {
            return {
                primary: colors["color-white"],
                label: labelPropIsOrchardColor
                    ? colors["color-ui-".concat(labelColor)]
                    : labelProp,
                labelHover: labelPropIsOrchardColor
                    ? colors["color-ui-".concat(labelColor, "-mid")]
                    : labelProp,
                labelActive: labelPropIsOrchardColor
                    ? colors["color-ui-".concat(labelColor, "-dark")]
                    : labelProp,
                altHover: colors["color-white"],
                altActive: labelPropIsOrchardColor
                    ? colors["color-ui-".concat(labelColor, "-80")]
                    : labelProp,
                hover: colors["color-white"],
                active: colors["color-white"],
                color: labelPropIsOrchardColor
                    ? colors["color-ui-".concat(labelColor)]
                    : labelProp,
            };
        }
        return {
            primary: colors["color-ui-".concat(color)],
            label: colors["color-white"],
            labelHover: colors["color-white"],
            labelActive: colors["color-white"],
            altHover: colors["color-ui-".concat(color, "-5")],
            altActive: colors["color-ui-".concat(color, "-20")],
            hover: colors["color-ui-".concat(color, "-mid")],
            active: colors["color-ui-".concat(color, "-dark")],
        };
    }, [color, colors, labelColor, labelProp, labelPropIsOrchardColor]);
    var buttonCssStyles = React.useMemo(function () {
        return {
            "--color-primary": buttonCssColors.primary,
            "--color-label": buttonCssColors.label,
            "--color-label-hover": buttonCssColors.labelHover,
            "--color-label-active": buttonCssColors.labelActive,
            "--color-alt-hover": buttonCssColors.altHover,
            "--color-alt-active": buttonCssColors.altActive,
            "--color-hover": buttonCssColors.hover,
            "--color-active": buttonCssColors.active,
        };
    }, [buttonCssColors]);
    var buttonContent = function () {
        var fillColor;
        if (color === "interface" || color === "white")
            fillColor = buttonCssColors.label;
        else if (variant === "primary")
            fillColor = colors["color-white"];
        else
            fillColor = buttonCssColors.primary;
        return (React.createElement(React.Fragment, null,
            React.createElement("span", { "aria-hidden": isSkeleton ? "true" : undefined, className: styles.label, "data-testid": "button-content" }, (children && React.createElement("div", { className: styles.children }, children)) ||
                label),
            React.createElement("span", { "aria-hidden": isSkeleton ? "true" : undefined, className: styles.icon, "data-testid": buttonProps["data-testid"]
                    ? "".concat(buttonProps["data-testid"], "-icon")
                    : "button-icon" },
                React.createElement(ReloadIcon, { fillColor: fillColor }))));
    };
    if (isPropsForLink(props) && !!to) {
        return (React.createElement(Link, __assign({ ref: ref, to: to, className: buttonClassNames, "aria-busy": isSkeleton, "aria-disabled": isDisabled, "aria-label": label, style: __assign(__assign({}, style), buttonCssStyles) }, buttonProps), buttonContent()));
    }
    return isPropsForAnchor(props) && !!href ? (React.createElement("a", __assign({ ref: ref, className: buttonClassNames, "aria-busy": isSkeleton, "aria-label": label, "aria-disabled": isDisabled, href: href, style: __assign(__assign({}, style), buttonCssStyles) }, buttonProps), buttonContent())) : (React.createElement("button", __assign({ ref: ref, className: buttonClassNames, "aria-busy": isSkeleton, "aria-label": label, disabled: isDisabled, style: __assign(__assign({}, style), buttonCssStyles) }, buttonProps), buttonContent()));
});
var buttonStyles = __assign({}, styles);

export { Button as B, ReloadIcon as R, buttonStyles as b };
